import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import KeystoreDialogFields from "../field/KeystoreDialogFields";
import FileInput, {File} from "../../../shared/components/FileInput";
import {Dropdown} from "primereact/dropdown";
import {Password} from "primereact/password";
import {KeyStore, KeystoreType} from "@softvision/webpdf-wsclient-9-typescript/lib/session/Administration";
import Administration from "../../session/Administration";
import {KeystoreViewer} from "../../../shared/components/KeystoreViewer";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import {
	Application,
	ApplicationCheckMode,
	ApplicationConfigKeystore,
	CertificateEntry,
	ConfigurationType,
	GlobalKeyStore,
	GlobalKeystoreFormat,
	GlobalKeyStoreInterface,
	SignatureApplicationCheck,
	SignatureApplicationCheckInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Info, {InfoType} from "../../../shared/components/Info";
import FunctionHelper from "../../../shared/helper/FunctionHelper";

interface KeystoreDialogProperties extends AdminDialogProperties<ApplicationConfigKeystore> {
}

interface KeystoreDialogStatus extends AdminDialogState<ApplicationConfigKeystore> {
	keystoreFile: File;
	keystoreSet: boolean;
	certificates?: Array<CertificateEntry>;
}

export class KeystoreDialogDefinition extends AdminDialog<ApplicationConfigKeystore, KeystoreDialogProperties,
	KeystoreDialogStatus> {
	constructor(props: Readonly<KeystoreDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			keystoreSet: false
		};
	}

	private renderCertificateContents(): ReactNode {
		if (!this.state.keystoreSet) {
			return null;
		}

		return (
			<>
				<div className="p-col-12">
					<KeystoreViewer {...this.props} certificates={this.state.certificates}/>
				</div>
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("keystore.new", {ns: "dialog"})}
							className="dialog-button"
							onClick={() => {
								this.onChange(KeystoreDialogFields.PASSWORD, "");

								this.setState({
									keystoreSet: false
								});
							}}/>
				</div>
			</>
		);
	}

	private renderFormContents(): ReactNode {
		let parameter: ApplicationConfigKeystore | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter) || this.state.keystoreSet) {
			return null;
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-type">
						<Translate {...this.props} data="keystore.type.label" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigKeystore.getTypeDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Dropdown inputId="control-type"
								  value={FunctionHelper.applyValue(
									  parameter?.type, ApplicationConfigKeystore.getTypeDefault()
								  )}
								  options={[
									  {
										  label: Translate.value("keystore.type.none", {ns: "dialog"}),
										  value: GlobalKeystoreFormat.NONE
									  },
									  {
										  label: Translate.value("keystore.type.jks", {ns: "dialog"}),
										  value: "JKS"
									  },
									  {
										  label: Translate.value("keystore.type.pkcs11", {ns: "dialog"}),
										  value: "PKCS11"
									  },
									  {
										  label: Translate.value("keystore.type.pkcs12", {ns: "dialog"}),
										  value: GlobalKeystoreFormat.PKCS12
									  }
								  ]}
								  onChange={(e) => this.onChange(KeystoreDialogFields.TYPE, e.value)}
								  appendTo={document.getElementById("root")}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={
						 parameter?.type === GlobalKeystoreFormat.NONE ||
						 parameter?.type === GlobalKeystoreFormat.PKCS11
					 }>
					<label className="p-col-12 p-sm-4" htmlFor="control-file">
						<Translate {...this.props} data="keystore.file" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8">
						<FileInput inputId="control-file" accept={parameter?.type === "JKS" ? ".jks" : ".p12, .pfx"}
								   value={this.state.keystoreFile.value} filename={this.state.keystoreFile.name}
								   onChange={(files) => this.onChange(KeystoreDialogFields.FILE, files[0])}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={parameter?.type === GlobalKeystoreFormat.NONE}>
					<label className="p-col-12 p-sm-4" htmlFor="control-password">
						<Translate {...this.props} data="keystore.password" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigKeystore.getPasswordDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Password inputId="control-password" feedback={false} toggleMask={true}
								  value={FunctionHelper.applyValue(
									  parameter?.password, ApplicationConfigKeystore.getPasswordDefault()
								  )}
								  onChange={(e) => this.onChange(
									  KeystoreDialogFields.PASSWORD, e.currentTarget.value
								  )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["keystore-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.keystore.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderFormContents()}
					{this.renderCertificateContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-keystore"}/>
					<Translate {...this.props} data="keystore.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: KeystoreDialogFields, value: any): void {
		let parameter: ApplicationConfigKeystore = this.state.parameter as ApplicationConfigKeystore;

		switch (field) {
			case KeystoreDialogFields.TYPE:
				parameter.type = value;
				parameter.password = ApplicationConfigKeystore.getPasswordDefault();
				break;
			case KeystoreDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let keystoreFile: File = value;

				this.setState({
					keystoreFile: keystoreFile
				});
				break;
			case KeystoreDialogFields.PASSWORD:
				parameter.password = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	private async isKeystoreSet(): Promise<void> {
		let administration: Administration = this.props.context.session.getAdministration();
		let signatureKeyStore: GlobalKeyStore =
			await administration.getKeystore(KeystoreType.Global) as GlobalKeyStore;

		this.setState({
			keystoreSet: FunctionHelper.applyValue(
				signatureKeyStore.certificates, GlobalKeyStore.getCertificatesDefault()
			).length > 0
		});
	}

	protected hasChanges(): boolean {
		let result: boolean = super.hasChanges();

		if (this.state.keystoreFile.value !== "") {
			result = true;
		}

		return result;
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();
		await this.isKeystoreSet();

		let administration: Administration = this.props.context.session.getAdministration();
		let signatureKeyStore: GlobalKeyStore =
			await administration.getKeystore(KeystoreType.Global) as GlobalKeyStore;
		let certificates: Array<CertificateEntry> = FunctionHelper.applyValue(
			signatureKeyStore.certificates, GlobalKeyStore.getCertificatesDefault()
		);

		this.setState({
			certificates: certificates
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: ApplicationConfigKeystore = this.state.parameter as ApplicationConfigKeystore;
		let administration: Administration = this.props.context.session.getAdministration();
		let applicationConfiguration: Application = (
			await administration.getConfiguration(ConfigurationType.Application) as Application
		).clone();
		applicationConfiguration.keystore = parameter.type === GlobalKeystoreFormat.NONE ? undefined : parameter;

		let currentKeystore: KeyStore = await administration.getKeystore(KeystoreType.Global);

		let checks: Array<SignatureApplicationCheck> = [];
		if (parameter.type !== GlobalKeystoreFormat.NONE) {
			checks.push(SignatureApplicationCheck.fromJson({
				checkType: ApplicationCheckMode.Signature
			} as SignatureApplicationCheckInterface));
		}

		try {
			administration.setKeystore(KeystoreType.Global, GlobalKeyStore.fromJson({
				keyStoreContent: parameter.type === GlobalKeystoreFormat.NONE ||
				parameter.type === GlobalKeystoreFormat.PKCS12 ?
					undefined : this.state.keystoreFile.value
			} as GlobalKeyStoreInterface));

			await administration.updateConfiguration(ConfigurationType.Application, applicationConfiguration, checks);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}

			// reset keystore
			administration.setKeystore(KeystoreType.Global, currentKeystore);
			return;
		}

		let message: Message = new Message("success.administration.keystore", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected hide() {
		this.setState({
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		});

		super.hide();
	}

	protected async reset(): Promise<void> {
		await super.reset();
		await this.isKeystoreSet();

		this.setState({
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		});
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.KEYSTORE;
	}
}

const KeystoreDialog = React.memo(KeystoreDialogDefinition,
	(prevProps: Readonly<KeystoreDialogProperties>, nextProps: Readonly<KeystoreDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default KeystoreDialog;